import { call, put, select } from 'redux-saga/effects';
import { hasPermissionsSaga } from 'features/permissions/sagas/hasPermissionsSaga';
import { hasDynamicPermissionsSaga } from 'features/permissions/sagas/hasDynamicPermissionsSaga';
import { PermissionTypes } from 'features/permissions/types';
import {
  broadcastPermissionGranted,
  broadcastPermissionRevoked,
  dynamicBroadcastPermissionsReset,
  signalingRoleChanged,
} from 'features/permissions/actions';
import { User, UserId } from 'features/users/types';
import { selectLocalUserId } from 'features/users/usersSlice';
import { selectIsAudioOnlyRoom } from 'features/room/roomSlice';
import { logger } from 'utils/logger';

interface BroadcastKindAccess {
  had: boolean;
  has: boolean;
}

function* checkBroadcastKindAccessSaga(
  permission: PermissionTypes.broadcastAudio | PermissionTypes.broadcastVideo,
  oldRole: string,
  newRole: string,
  user: User
) {
  const oldRoleAllowed: boolean = yield call(hasPermissionsSaga, permission, { role: oldRole });
  const newRoleAllowed: boolean = yield call(hasPermissionsSaga, permission, { role: newRole });
  const userHadDynamic: boolean = yield call(hasDynamicPermissionsSaga, permission, {
    targetUser: user,
  });

  return { had: oldRoleAllowed || userHadDynamic, has: newRoleAllowed };
}

export function* handleRoleChangeBroadcastPermissionsSaga(
  payload: ReturnType<typeof signalingRoleChanged>['payload'],
  user: User
) {
  const { role, id } = payload;
  const { role: oldRole } = user;

  const audio: BroadcastKindAccess = yield call(
    checkBroadcastKindAccessSaga,
    PermissionTypes.broadcastAudio,
    oldRole,
    role,
    user
  );
  const video: BroadcastKindAccess = yield call(
    checkBroadcastKindAccessSaga,
    PermissionTypes.broadcastVideo,
    oldRole,
    role,
    user
  );
  const isAudioOnlyRoom: boolean = yield select(selectIsAudioOnlyRoom);

  const hadAudio = audio.had;
  const hadVideo = video.had && !isAudioOnlyRoom;
  const hasAudio = audio.has;
  const hasVideo = video.has && !isAudioOnlyRoom;

  const permissionsGranted = (hasAudio && !hadAudio) || (hasVideo && !hadVideo);
  const permissionsRevoked = (hadAudio || hadVideo) && !(hasAudio || hasVideo);

  yield put(dynamicBroadcastPermissionsReset({ id }));

  const localUserId: UserId = yield select(selectLocalUserId);
  if (localUserId === id) {
    let outcome = 'unchanged';
    if (permissionsGranted) {
      outcome = 'granted';
    } else if (permissionsRevoked) {
      outcome = 'revoked';
    }

    logger
      .remote({ tier: 1 })
      .info(`Role change broadcast access ${outcome}`, { hadAudio, hadVideo, hasAudio, hasVideo });
  }

  if (permissionsGranted) {
    yield put(
      broadcastPermissionGranted({
        id: payload.id,
        initiator: payload.initiator,
        audio: true,
        video: !isAudioOnlyRoom,
      })
    );
    return;
  }

  if (permissionsRevoked) {
    yield put(broadcastPermissionRevoked(payload));
  }
}
